import React, { useEffect, useRef, useState } from "react";
import { Bell, BellRinging, Alarm } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
const pad = (n) => String(n).padStart(2, "0");
const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date(Date.now() + 3600000);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });

function useNow(ms = 15000) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

/** Form per impostare/modificare la sveglia personale sulla pratica. */
export function ReminderDialog({ open, onOpenChange, practiceId, reminder, onSaved }) {
  const [description, setDescription] = useState("");
  const [when, setWhen] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setDescription(reminder?.description || "");
    setWhen(toLocalInput(reminder?.remind_at));
  }, [open, reminder]);

  const save = async () => {
    if (!description.trim() || !when) { toast.error("Inserisci descrizione, data e ora"); return; }
    setSaving(true);
    try {
      const r = await api.put(`/practices/${practiceId}/reminder`, {
        description: description.trim(),
        remind_at: new Date(when).toISOString(),
      });
      toast.success("Reminder impostato");
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio reminder");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onClick={(e) => e.stopPropagation()} data-testid="reminder-dialog">
        <DialogHeader><DialogTitle>Imposta reminder</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Descrizione</label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Es. Richiamare il cliente per i documenti" data-testid="reminder-description-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Data e ora</label>
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
              data-testid="reminder-datetime-input" />
          </div>
          <p className="text-[11px] text-muted-foreground">Il reminder è personale: lo vedi solo tu.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reminder-cancel-btn">Annulla</Button>
          <Button onClick={save} disabled={saving} className="ht-gradient border-0" data-testid="reminder-save-btn">
            {saving ? "Salvataggio..." : "Salva reminder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Icona reminder nella card: sveglia da impostare, programmata o scaduta (campanella che vibra). */
export function PracticeReminderBell({ practiceId, reminder: initial }) {
  const [reminder, setReminder] = useState(initial || null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [popOpen, setPopOpen] = useState(false);
  const now = useNow();
  const closeTimer = useRef(null);
  useEffect(() => { setReminder(initial || null); }, [initial]);
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  const openHover = () => { clearTimeout(closeTimer.current); setPopOpen(true); };
  const closeHover = () => { closeTimer.current = setTimeout(() => setPopOpen(false), 200); };

  const due = !!reminder && new Date(reminder.remind_at).getTime() <= now;

  const disable = async (e) => {
    stop(e);
    try {
      await api.delete(`/practices/${practiceId}/reminder`);
      setReminder(null);
      setPopOpen(false);
      toast.success("Reminder disattivato");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const dialog = (
    <ReminderDialog open={dialogOpen} onOpenChange={setDialogOpen} practiceId={practiceId}
      reminder={reminder} onSaved={setReminder} />
  );

  if (!reminder) {
    return (
      <>
        <button type="button" onClick={(e) => { stop(e); setDialogOpen(true); }}
          className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors shrink-0"
          title="Imposta reminder" data-testid={`reminder-set-btn-${practiceId}`}>
          <Alarm size={14} />
        </button>
        {dialog}
      </>
    );
  }

  return (
    <>
      <Popover open={popOpen} onOpenChange={setPopOpen}>
        <PopoverTrigger asChild>
          <button type="button"
            onClick={(e) => { stop(e); setPopOpen((o) => !o); }}
            onMouseEnter={openHover}
            onMouseLeave={closeHover}
            className={`p-1 rounded-md shrink-0 transition-colors ${due ? "text-red-600 bg-red-50" : "text-amber-600 hover:bg-amber-50"}`}
            data-testid={due ? `reminder-due-bell-${practiceId}` : `reminder-scheduled-${practiceId}`}>
            {due
              ? <BellRinging size={16} weight="fill" className="vibrate-bell" />
              : <Bell size={14} weight="bold" />}
          </button>
        </PopoverTrigger>
        <PopoverContent side="top" className="w-64 p-3" onClick={(e) => e.stopPropagation()}
          onMouseEnter={openHover} onMouseLeave={closeHover} data-testid={`reminder-tooltip-${practiceId}`}>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
            {due ? "Reminder scaduto" : "Reminder"} · {fmt(reminder.remind_at)}
          </div>
          <p className="text-sm mt-1 whitespace-pre-wrap break-words" data-testid={`reminder-description-${practiceId}`}>
            {reminder.description}
          </p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={disable}
              data-testid={`reminder-disable-btn-${practiceId}`}>Disattiva</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs"
              onClick={(e) => { stop(e); setPopOpen(false); setDialogOpen(true); }}
              data-testid={`reminder-edit-btn-${practiceId}`}>Modifica</Button>
          </div>
        </PopoverContent>
      </Popover>
      {dialog}
    </>
  );
}
